import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';
import { useState } from 'react';

import { formatPhone } from '@seccon/shared/utils/format';

function setEmployee (employee, setValue, trigger) {
  setValue('employee.companyName', employee?.facility?.facilityName ?? '');
  setValue('employee.jobTitle', employee?.jobTitle ?? '');
  setValue('employee.companyPhoneNumber', formatPhone(employee?.phoneCompany ?? ''));
  setValue('employee.companyEmail', employee?.emailCompany ?? '');
  setValue('foreignTravel.travelingPersonnel.phoneCell', formatPhone(employee?.phoneCell ?? ''));
  setValue('foreignTravel.travelingPersonnel.emailPersonal', employee?.emailPersonal ?? '');

  setValue('foreignTravel.travelingPersonnel.passport.number', employee.passport?.number ?? '');
  setValue('foreignTravel.travelingPersonnel.passport.type', employee.passport?.type ?? '');
  setValue('foreignTravel.travelingPersonnel.passport.stateOfBirth', employee.passport?.stateOfBirth ?? '');
  setValue('foreignTravel.travelingPersonnel.passport.countryOfBirth', employee.passport?.countryOfBirth ?? '');
  setValue('foreignTravel.travelingPersonnel.passport.issueDate', employee.passport?.issueDate ? dayjs(employee.passport?.issueDate) : '');
  setValue('foreignTravel.travelingPersonnel.passport.expirationDate', employee.passport?.expirationDate ? dayjs(employee.passport?.expirationDate) : '');

  for (let index = 0; index < employee.emergencyContacts?.length ?? 0; index++) {
    const contact = employee.emergencyContacts[index];

    setValue(`foreignTravel.emergencyContacts[${ index }].name`, contact.name ?? '');
    setValue(`foreignTravel.emergencyContacts[${ index }].streetAddress`, contact.streetAddress ?? '');
    setValue(`foreignTravel.emergencyContacts[${ index }].streetAddress2`, contact.streetAddress2 ?? '');
    setValue(`foreignTravel.emergencyContacts[${ index }].city`, contact.city ?? '');
    setValue(`foreignTravel.emergencyContacts[${ index }].state`, contact.state ?? '');
    setValue(`foreignTravel.emergencyContacts[${ index }].zip`, contact.zip ?? '');
    setValue(`foreignTravel.emergencyContacts[${ index }].name`, contact.name ?? '');
    setValue(`foreignTravel.emergencyContacts[${ index }].relationship`, contact.relationship ?? '');
    setValue(`foreignTravel.emergencyContacts[${ index }].phoneHome`, formatPhone(contact.phoneHome ?? ''));
    setValue(`foreignTravel.emergencyContacts[${ index }].phoneWork`, formatPhone(contact.phoneWork ?? ''));
    setValue(`foreignTravel.emergencyContacts[${ index }].phoneMobile`, formatPhone(contact.phoneMobile ?? ''));
  }

  trigger();
}

async function fetchEmployee (id, setValue, trigger) {
  if (!id) {
    return null;
  }

  try {
    const url = `/foreignTravel/personnel/${ id }`;
    const response = await fetch(url);
    const employee = await response.json();

    setEmployee(employee, setValue, trigger);

    return employee;
  } catch (error) {
    console.log('Failed to fetch foreignTravel personnel', error);
  }

  return true;
}

const useEmployeeInfo = () => {
  const { setValue, trigger } = useFormContext();

  /*
   * Rather than listening for changes to employee id in the form state, we need to fetch employee info at specific times:
   * 1. When creating a foreign travel, we want to fetch the employee info on mount once the current user info is fetched
   * 2. When creating or editing a foreign travel, we want to fetch the employee info whenever the Employee Name field changes
   */
  const [employeeIdToFetch, setEmployeeIdToFetch] = useState(null);

  useQuery({
    queryKey: ['useEmployee', employeeIdToFetch, setValue, trigger],
    queryFn: () => fetchEmployee(employeeIdToFetch, setValue, trigger),
    enabled: !!employeeIdToFetch,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  function fetchEmployeeInfo (selection) {
    setEmployeeIdToFetch(selection.id);
  }

  return fetchEmployeeInfo;
};

export default useEmployeeInfo;
